import { addMilliseconds, differenceInMilliseconds, isAfter, isBefore, isEqual, isWeekend, parse, set } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { IConfigNetBroker } from '../../config';
import { NonTradingDaysOfMarket } from '../../state';
import { formatDateToRequestDay, isDefined } from '../../utils';
export type OpenMarketState = {
  status: 'open';
  opens: Date;
  closes: Date;
  hours: MarketOpenHours;
};

export type ClosedMarketState = {
  status: 'closed';
  opens?: Date;
  closes?: Date;
  hours: MarketOpenHours;
};

export type KnownMarketState = OpenMarketState | ClosedMarketState;

export type UnknownMarketState = {
  status: 'unknown';
};

export type MarketState = KnownMarketState | UnknownMarketState;

export type MarketStatus = MarketState['status'];

export type TimeOfDay = {
  hours: number;
  minutes: number;
};

export type MarketOpenHours = {
  timeZone: string;
  opens: TimeOfDay;
  closes: TimeOfDay;
};

export type MarketsConfig = IConfigNetBroker['Markets']['markets'];
export type MarketName = keyof MarketsConfig;

// From Péter Varga:
// nasdaq, nyse: 15:30-22:00
// bet, beta, xtend: 9:00-17:00
// frankfurt, xetra, wien: 9:00-17:30

const americanHours: MarketOpenHours = {
  opens: { hours: 9, minutes: 30 },
  closes: { hours: 16, minutes: 0 },
  timeZone: 'America/New_York',
};

const hungarianHours: MarketOpenHours = {
  opens: { hours: 9, minutes: 0 },
  closes: { hours: 17, minutes: 0 },
  timeZone: 'Europe/Budapest',
};

const westernEuropeanHours: MarketOpenHours = {
  opens: { hours: 9, minutes: 0 },
  closes: { hours: 17, minutes: 30 },
  timeZone: 'Europe/Berlin',
};

// Hard-coded opening hours for the big markets/regions
const mainMarketBasedOpenHours: Record<MarketName, MarketOpenHours | undefined> = {
  bet: hungarianHours,
  beta: hungarianHours,
  xtend: hungarianHours,
  frankfurt: westernEuropeanHours,
  xetra: westernEuropeanHours,
  wien: westernEuropeanHours,
  nasdaq: americanHours,
  nyse: americanHours,

  // Can't trade/we don't want to show chart
  london: undefined,
  otc: undefined,
};

// In case any specific mic has different open hours, put it here,
// as this will take precedence over the generic ones.
const micBasedOpenHours: Record<string, MarketOpenHours> = {};

export function getOpenHoursForMic(mic: string, config: MarketsConfig): MarketOpenHours | undefined {
  const micBasedHours = micBasedOpenHours[mic];
  if (isDefined(micBasedHours)) {
    return micBasedHours;
  }
  const [mainMarket] = Object.entries(config).find(([_, mics]) => mics.includes(mic)) ?? [];
  if (!isDefined(mainMarket)) {
    return undefined;
  }
  return mainMarketBasedOpenHours[mainMarket as MarketName];
}

function getTimezoneOffset(reference: Date, { timeZone }: MarketOpenHours): number {
  const formatterTemplate = 'yyyy. MM. dd. HH:mm:ss,SSS';
  const formattedTimeInTz = formatInTimeZone(reference, timeZone, formatterTemplate);
  const tzDateLocally = parse(formattedTimeInTz, formatterTemplate, reference);
  return differenceInMilliseconds(reference, tzDateLocally);
}

function getAdjustedTime(today: Date, timeOfDay: TimeOfDay, offset: number): Date {
  const timeOfDayToday = set(today, {
    minutes: timeOfDay.minutes,
    hours: timeOfDay.hours,
  });
  return addMilliseconds(timeOfDayToday, offset);
}

export function getOpeningAndClosingTime(today: Date, hours: MarketOpenHours): [Date, Date] {
  const offset = getTimezoneOffset(today, hours);
  const todaysOpening = getAdjustedTime(today, hours.opens, offset);
  const todaysClosing = getAdjustedTime(today, hours.closes, offset);
  return [todaysOpening, todaysClosing];
}

export function getMarketState(
  config: MarketsConfig,
  mic: string,
  time: Date,
  today: Date,
  nonTradingDays: NonTradingDaysOfMarket,
): MarketState {
  const hours = getOpenHoursForMic(mic, config);
  if (!isDefined(hours)) {
    return { status: 'unknown' };
  }

  if (nonTradingDays.includes(formatDateToRequestDay(time)) || isWeekend(time)) {
    return { status: 'closed', hours };
  }

  const [opens, closes] = getOpeningAndClosingTime(today, hours);
  const isAfterOpeningTime = isAfter(time, opens) || isEqual(time, opens);
  const isBeforeClosingTime = isBefore(time, closes) || isEqual(time, closes);

  if (isAfterOpeningTime && isBeforeClosingTime) {
    return { status: 'open', closes, opens, hours };
  }

  return { status: 'closed', closes, opens, hours };
}
